import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc,
  query,
  where,
  Timestamp,
  increment
} from 'firebase/firestore';
import { db } from '../config/firebase.config';

export interface Earning {
  id: string;
  riderId: string;
  amount: number;
  type: 'delivery' | 'bonus' | 'tip' | 'quest_reward' | 'referral';
  jobId?: string;
  description: string;
  createdAt: Date;
}

export interface Withdrawal {
  id: string;
  riderId: string;
  amount: number;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  bankName: string;
  accountNumber: string;
  accountName: string;
  reference: string;
  requestedAt: Date;
  processedAt?: Date;
  failureReason?: string;
}

export interface WalletBalance {
  total: number;
  today: number;
  week: number;
  month: number;
  pending: number; // Pending withdrawals
  available: number; // Available for withdrawal
  todayEarnings?: number; // Alias for today used in some screens
}

export class WalletService {
  
  /**
   * Get rider's wallet balance
   */
  async getWalletBalance(riderId: string): Promise<WalletBalance> {
    try {
      const riderRef = doc(db, 'riders', riderId);
      const riderSnap = await getDoc(riderRef);

      if (!riderSnap.exists()) {
        // Return zero balance for new riders instead of throwing error
        console.log('⚠️ Rider profile not found, returning zero balance');
        return {
          total: 0,
          today: 0,
          todayEarnings: 0,
          week: 0,
          month: 0,
          pending: 0,
          available: 0
        };
      }

      const rider = riderSnap.data();
      const riderEarningsSnap = await getDoc(doc(db, 'rider_earnings', riderId));
      const riderEarnings = riderEarningsSnap.exists() ? riderEarningsSnap.data() : {};
      const legacyEarnings = rider.earnings || {};
      const earnings = {
        total: Number(riderEarnings.totalEarned ?? legacyEarnings.total) || 0,
        today: Number(legacyEarnings.today) || 0,
        week: Number(legacyEarnings.week) || 0,
        month: Number(legacyEarnings.month) || 0,
      };

      // Calculate pending withdrawals client-side so we don't need a composite index
      const withdrawalsRef = collection(db, 'withdrawals');
      const allWithdrawalsQuery = query(
        withdrawalsRef,
        where('riderId', '==', riderId)
      );
      const pendingSnap = await getDocs(allWithdrawalsQuery);
      const pending = pendingSnap.docs
        .map(doc => doc.data())
        .filter(data => ['pending', 'processing'].includes(data.status))
        .reduce((sum, data) => sum + (Number(data.amount) || 0), 0);

      const todayVal = earnings.today || 0;
      return {
        total: earnings.total || 0,
        today: todayVal,
        todayEarnings: todayVal,
        week: earnings.week || 0,
        month: earnings.month || 0,
        pending,
        available: Number(riderEarnings.availableBalance ?? ((earnings.total || 0) - pending)) || 0
      };
    } catch (error) {
      console.error('Error getting wallet balance:', error);
      throw error;
    }
  }

  /**
   * Add earnings to rider's wallet
   */
  async addEarning(
    riderId: string, 
    amount: number, 
    type: 'delivery' | 'bonus' | 'tip' | 'quest_reward' | 'referral',
    description: string,
    jobId?: string
  ): Promise<string> {
    try {
      // Create earning record
      const earningRef = doc(collection(db, 'earnings'));
      await setDoc(earningRef, {
        riderId,
        amount,
        type,
        description,
        jobId: jobId || null,
        createdAt: Timestamp.now(),
      });

      // Update rider's earnings using increment
      const riderRef = doc(db, 'riders', riderId);
      await updateDoc(riderRef, {
        'earnings.total': increment(amount),
        'earnings.today': increment(amount),
        'earnings.week': increment(amount),
        'earnings.month': increment(amount),
      });

      // Mirror to the top-level rider_earnings doc so the admin/customer app stays in sync
      const riderEarningsRef = doc(db, 'rider_earnings', riderId);
      await setDoc(riderEarningsRef, {
        riderId,
        totalEarned: increment(amount),
        availableBalance: increment(amount),
        completedDeliveries: increment(1),
        deliveryCount: increment(1),
        lastDeliveryCompleted: Timestamp.now(),
        lastUpdated: Timestamp.now(),
        updatedAt: Timestamp.now(),
        createdAt: Timestamp.now(),
      }, { merge: true });

      console.log(`✅ Added ₦${amount} to rider ${riderId} wallet`);
      return earningRef.id;
    } catch (error) {
      console.error('Error adding earning:', error);
      throw error;
    }
  }

  /**
   * Get rider's earning history
   */
  async getEarningHistory(riderId: string, limitCount: number = 50): Promise<Earning[]> {
    try {
      const earningsRef = collection(db, 'earnings');
      // Single-field query to avoid needing a composite index; sort client-side
      const q = query(
        earningsRef,
        where('riderId', '==', riderId)
      );

      const querySnapshot = await getDocs(q);
      const all = querySnapshot.docs.map(docSnap => {
        const data = docSnap.data();
        const createdAt = data.createdAt?.toDate
          ? data.createdAt.toDate()
          : data.createdAt ? new Date(data.createdAt) : new Date();
        return {
          id: docSnap.id,
          riderId: data.riderId,
          amount: Number(data.amount) || 0,
          type: (data.type as Earning['type']) || 'delivery',
          jobId: data.jobId,
          description: data.description || '',
          createdAt,
        };
      });

      const seenJobIds = new Set(all.map((earning) => earning.jobId).filter(Boolean));
      for (const collectionName of ['deliveries', 'bookings', 'orders'] as const) {
        try {
          const jobs = await getDocs(query(collection(db, collectionName), where('riderId', '==', riderId)));
          jobs.docs.forEach((jobDoc) => {
            const data = jobDoc.data();
            const status = String(data.status || data.orderStatus || '').toLowerCase();
            const amount = Number(
              data.riderPayout ?? data.riderEarnings ?? data.feeBreakdown?.riderPayout ?? data.earnings
            ) || 0;
            if (!['delivered', 'completed'].includes(status) || amount <= 0 || seenJobIds.has(jobDoc.id)) return;
            const rawDate = data.deliveredAt || data.completedAt || data.updatedAt || data.createdAt;
            const createdAt = rawDate?.toDate ? rawDate.toDate() : rawDate ? new Date(rawDate) : new Date();
            all.push({
              id: `job-${collectionName}-${jobDoc.id}`,
              riderId,
              amount,
              type: 'delivery',
              jobId: jobDoc.id,
              description: `Delivery earnings · ${jobDoc.id}`,
              createdAt,
            });
            seenJobIds.add(jobDoc.id);
          });
        } catch (error) {
          console.warn(`Could not load ${collectionName} earnings:`, error);
        }
      }

      return all
        .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
        .slice(0, limitCount);
    } catch (error) {
      console.error('Error getting earning history:', error);
      return [];
    }
  }

  /**
   * Get rider statistics including completed deliveries and earnings by vehicle type
   */
  async getRiderStats(riderId: string): Promise<{
    completedDeliveries: number;
    bikeEarnings: number;
    bicycleEarnings: number;
    legEarnings: number;
  }> {
    try {
      const riderRef = doc(db, 'riders', riderId);
      const riderSnap = await getDoc(riderRef);
      
      if (!riderSnap.exists()) {
        return {
          completedDeliveries: 0,
          bikeEarnings: 0,
          bicycleEarnings: 0,
          legEarnings: 0,
        };
      }
      
      const rider = riderSnap.data();
      const byVehicleType = rider.earnings?.byVehicleType || { bike: 0, bicycle: 0, leg: 0 };
      
      return {
        completedDeliveries: rider.completedDeliveries || 0,
        bikeEarnings: byVehicleType.bike || 0,
        bicycleEarnings: byVehicleType.bicycle || 0,
        legEarnings: byVehicleType.leg || 0,
      };
    } catch (error) {
      console.error('Error getting rider stats:', error);
      return {
        completedDeliveries: 0,
        bikeEarnings: 0,
        bicycleEarnings: 0,
        legEarnings: 0,
      };
    }
  }

  /**
   * Request withdrawal
   */
  async requestWithdrawal(
    riderId: string,
    amount: number,
    bankDetails: {
      bankName: string;
      accountNumber: string;
      accountName: string;
    }
  ): Promise<string> {
    try {
      // Check available balance
      const balance = await this.getWalletBalance(riderId);
      
      if (balance.available < amount) {
        throw new Error('Insufficient balance');
      }

      if (amount < 1000) {
        throw new Error('Minimum withdrawal amount is ₦1,000');
      }

      // Create withdrawal request
      const withdrawalRef = doc(collection(db, 'withdrawals'));
      const reference = `WD-${Date.now()}-${riderId.substring(0, 6)}`;
      
      await setDoc(withdrawalRef, {
        riderId,
        amount,
        status: 'pending',
        bankName: bankDetails.bankName,
        accountNumber: bankDetails.accountNumber,
        accountName: bankDetails.accountName,
        reference,
        requestedAt: Timestamp.now(),
      });

      // Mirror pending state to admin's rider_earnings view
      const riderEarningsRef = doc(db, 'rider_earnings', riderId);
      await setDoc(riderEarningsRef, {
        availableBalance: increment(-amount),
        pendingWithdrawals: increment(amount),
        lastUpdated: Timestamp.now(),
        updatedAt: Timestamp.now(),
      }, { merge: true });

      console.log(`✅ Withdrawal request created: ${reference} for ₦${amount}`);
      return withdrawalRef.id;
    } catch (error) {
      console.error('Error requesting withdrawal:', error);
      throw error;
    }
  }

  /**
   * Get withdrawal history
   */
  async getWithdrawalHistory(riderId: string, limitCount: number = 50): Promise<Withdrawal[]> {
    try {
      const withdrawalsRef = collection(db, 'withdrawals');
      // Single-field query to avoid needing a composite index; sort client-side
      const q = query(
        withdrawalsRef,
        where('riderId', '==', riderId)
      );

      const querySnapshot = await getDocs(q);
      const all = querySnapshot.docs.map(docSnap => {
        const data = docSnap.data();
        const requestedAt = data.requestedAt?.toDate
          ? data.requestedAt.toDate()
          : data.requestedAt ? new Date(data.requestedAt) : new Date();
        const processedAt = data.processedAt?.toDate
          ? data.processedAt.toDate()
          : data.processedAt ? new Date(data.processedAt) : undefined;
        return {
          id: docSnap.id,
          riderId: data.riderId,
          amount: Number(data.amount) || 0,
          status: (data.status as Withdrawal['status']) || 'pending',
          bankName: data.bankName || '',
          accountNumber: data.accountNumber || '',
          accountName: data.accountName || '',
          reference: data.reference || '',
          requestedAt,
          processedAt,
          failureReason: data.failureReason,
        };
      });

      return all
        .sort((a, b) => b.requestedAt.getTime() - a.requestedAt.getTime())
        .slice(0, limitCount);
    } catch (error) {
      console.error('Error getting withdrawal history:', error);
      return [];
    }
  }

  /**
   * Process withdrawal (Admin/Backend function)
   * This would typically be called by a Cloud Function
   */
  async processWithdrawal(withdrawalId: string, success: boolean, failureReason?: string): Promise<void> {
    try {
      const withdrawalRef = doc(db, 'withdrawals', withdrawalId);
      const withdrawalSnap = await getDoc(withdrawalRef);
      
      if (!withdrawalSnap.exists()) {
        throw new Error('Withdrawal not found');
      }

      const withdrawal = withdrawalSnap.data();
      
      if (success) {
        // Mark as completed
        await updateDoc(withdrawalRef, {
          status: 'completed',
          processedAt: Timestamp.now(),
        });

        // Deduct from rider's earnings
        const riderRef = doc(db, 'riders', withdrawal.riderId);
        await updateDoc(riderRef, {
          'earnings.total': increment(-withdrawal.amount),
        });

        // Mirror to admin's rider_earnings view
        const riderEarningsRef = doc(db, 'rider_earnings', withdrawal.riderId);
        await setDoc(riderEarningsRef, {
          pendingWithdrawals: increment(-withdrawal.amount),
          totalWithdrawn: increment(withdrawal.amount),
          lastUpdated: Timestamp.now(),
          updatedAt: Timestamp.now(),
        }, { merge: true });

        console.log(`✅ Withdrawal ${withdrawalId} processed successfully`);
      } else {
        // Mark as failed
        await updateDoc(withdrawalRef, {
          status: 'failed',
          processedAt: Timestamp.now(),
          failureReason: failureReason || 'Payment failed',
        });

        // Refund the held balance in admin's rider_earnings view
        const riderEarningsRef = doc(db, 'rider_earnings', withdrawal.riderId);
        await setDoc(riderEarningsRef, {
          pendingWithdrawals: increment(-withdrawal.amount),
          availableBalance: increment(withdrawal.amount),
          lastUpdated: Timestamp.now(),
          updatedAt: Timestamp.now(),
        }, { merge: true });

        console.log(`❌ Withdrawal ${withdrawalId} failed: ${failureReason}`);
      }
    } catch (error) {
      console.error('Error processing withdrawal:', error);
      throw error;
    }
  }

  /**
   * Cancel withdrawal (rider can cancel pending withdrawals)
   */
  async cancelWithdrawal(withdrawalId: string, riderId: string): Promise<void> {
    try {
      const withdrawalRef = doc(db, 'withdrawals', withdrawalId);
      const withdrawalSnap = await getDoc(withdrawalRef);
      
      if (!withdrawalSnap.exists()) {
        throw new Error('Withdrawal not found');
      }

      const withdrawal = withdrawalSnap.data();
      
      if (withdrawal.riderId !== riderId) {
        throw new Error('Unauthorized');
      }

      if (withdrawal.status !== 'pending') {
        throw new Error('Cannot cancel withdrawal in ' + withdrawal.status + ' status');
      }

      await updateDoc(withdrawalRef, {
        status: 'cancelled',
        processedAt: Timestamp.now(),
      });

      // Release the held balance in admin's rider_earnings view
      const riderEarningsRef = doc(db, 'rider_earnings', withdrawal.riderId);
      await setDoc(riderEarningsRef, {
        pendingWithdrawals: increment(-withdrawal.amount),
        availableBalance: increment(withdrawal.amount),
        lastUpdated: Timestamp.now(),
        updatedAt: Timestamp.now(),
      }, { merge: true });

      console.log(`✅ Withdrawal ${withdrawalId} cancelled by rider`);
    } catch (error) {
      console.error('Error cancelling withdrawal:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const walletService = new WalletService();
